import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const gridVariants = cva('grid', {
  variants: {
    cols: {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      6: 'grid-cols-6',
      12: 'grid-cols-12',
    },
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      6: 'gap-6',
      8: 'gap-8',
      12: 'gap-12',
    },
    gapX: {
      0: 'gap-x-0',
      1: 'gap-x-1',
      2: 'gap-x-2',
      3: 'gap-x-3',
      4: 'gap-x-4',
      6: 'gap-x-6',
      8: 'gap-x-8',
      12: 'gap-x-12',
    },
    gapY: {
      0: 'gap-y-0',
      1: 'gap-y-1',
      2: 'gap-y-2',
      3: 'gap-y-3',
      4: 'gap-y-4',
      6: 'gap-y-6',
      8: 'gap-y-8',
      12: 'gap-y-12',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    },
  },
  defaultVariants: {
    cols: 1,
  },
});

type GapScale = NonNullable<VariantProps<typeof gridVariants>['gap']>;

const DEFAULT_GAP: GapScale = 4;

// gloss
const resolveGaps = (gap?: GapScale | null, gapX?: GapScale | null, gapY?: GapScale | null) => {
  if (gapX == null && gapY == null) return { gap: gap ?? DEFAULT_GAP };
  return { gapX: gapX ?? gap ?? DEFAULT_GAP, gapY: gapY ?? gap ?? DEFAULT_GAP };
};

type Breakpoint = 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type ColCount = 1 | 2 | 3 | 4 | 6 | 12;
type ResponsiveCols = Partial<Record<Breakpoint, ColCount>>;

// why: column classes must stay a static lookup — building the class strings dynamically means
// why: Tailwind's scanner never generates them
const responsiveColClasses: Record<Breakpoint, Record<ColCount, string>> = {
  base: {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    6: 'grid-cols-6',
    12: 'grid-cols-12',
  },
  sm: {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
    6: 'sm:grid-cols-6',
    12: 'sm:grid-cols-12',
  },
  md: {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
    6: 'md:grid-cols-6',
    12: 'md:grid-cols-12',
  },
  lg: {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    6: 'lg:grid-cols-6',
    12: 'lg:grid-cols-12',
  },
  xl: {
    1: 'xl:grid-cols-1',
    2: 'xl:grid-cols-2',
    3: 'xl:grid-cols-3',
    4: 'xl:grid-cols-4',
    6: 'xl:grid-cols-6',
    12: 'xl:grid-cols-12',
  },
  '2xl': {
    1: '2xl:grid-cols-1',
    2: '2xl:grid-cols-2',
    3: '2xl:grid-cols-3',
    4: '2xl:grid-cols-4',
    6: '2xl:grid-cols-6',
    12: '2xl:grid-cols-12',
  },
};

const buildResponsiveColClasses = (cols: ResponsiveCols) =>
  Object.entries(cols)
    .map(([bp, count]) => responsiveColClasses[bp as Breakpoint][count as ColCount])
    .join(' ');

// gloss
type GridProps = Omit<React.ComponentProps<'div'>, 'cols'> &
  Omit<VariantProps<typeof gridVariants>, 'cols'> & {
    cols?: ColCount | ResponsiveCols;
    columns?: string;
  };

const Grid = ({ className, cols = 1, gap, gapX, gapY, align, columns, style, ...props }: GridProps) => {
  const gaps = resolveGaps(gap, gapX, gapY);

  if (columns) {
    return (
      <div
        data-slot="grid"
        className={cn(gridVariants({ cols: null, ...gaps, align, className: undefined }), className)}
        style={{ ...style, gridTemplateColumns: columns }}
        {...props}
      />
    );
  }

  const isResponsive = typeof cols === 'object';
  const classes = isResponsive
    ? cn(
        'grid',
        gridVariants({ cols: null, ...gaps, align, className: undefined }),
        buildResponsiveColClasses(cols),
        className,
      )
    : cn(gridVariants({ cols, ...gaps, align, className }));

  return <div data-slot="grid" className={classes} style={style} {...props} />;
};

type GridRowProps = React.ComponentProps<'div'>;

// gloss
const GridRow = ({ className, ...props }: GridRowProps) => (
  <div data-slot="grid-row" className={cn('col-span-full grid grid-cols-subgrid', className)} {...props} />
);

export type { ColCount, GapScale, GridProps, GridRowProps, ResponsiveCols };
export { Grid, GridRow, gridVariants };
